// components/ActivityTracker/ActivityBulkUpload.jsx
import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Button, Text, Badge, Spinner,
  Table, TableBody, TableCell,
  TableHeader, TableHeaderCell, TableRow, Tooltip,
} from "@fluentui/react-components";
import {
  ArrowDownload24Regular, ArrowUpload24Regular, Dismiss24Regular,
  CheckmarkCircle20Regular, ErrorCircle20Regular, Warning24Regular,
  ArrowLeft24Regular, DocumentBulletList24Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useActivityTracker } from "../../hooks/useActivityTracker";

const TEMPLATE_HEADERS = [
  "Description*",
  "User Department*",
  "Entity/Asset",
  "Contractor/Vendor",
  "Category",
  "Priority",
  "Status",
  "Due Date (YYYY-MM-DD)",
  "Look Ahead",
  "Additional Notes",
  "Assigned To Email",
  "Assigned To Name",
  "Owner Email",
  "Owner Name",
];

const EXAMPLE_ROW = [
  "Contract for Provision of Turnkey Drilling Services",
  "WELL ENGINEERING",
  "276",
  "Bill Energy Services Limited",
  "Contracts",
  "High",
  "In Progress",
  "2024-03-31",
  "Follow up with NUIMS rep for MoM sign-off and close-out.",
  "Turnkey drilling services contract for OPL 276 Uda well.",
  "staff@lekoil.com",
  "John Doe",
  "head@lekoil.com",
  "Well Engineering Head",
];

const HEADER_FIELD_MAP = {
  "description*": "title",
  "description": "title",
  "user department*": "userDept",
  "user department": "userDept",
  "entity/asset": "entity",
  "entity": "entity",
  "contractor/vendor": "contractor",
  "contractor": "contractor",
  "category": "category",
  "priority": "priority",
  "status": "status",
  "due date (yyyy-mm-dd)": "dueDate",
  "due date": "dueDate",
  "look ahead": "lookAhead",
  "additional notes": "description",
  "notes": "description",
  "assigned to email": "assignedTo",
  "assigned to name": "assignedToName",
  "owner email": "ownerEmail",
  "owner name": "ownerName",
};

function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, EXAMPLE_ROW]);
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 30 }));
  XLSX.utils.book_append_sheet(wb, ws, "Activities");
  XLSX.writeFile(wb, "LekOil_Activities_BulkUpload_Template.xlsx");
}

function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: "yyyy-mm-dd", defval: "" });
        resolve(rows);
      } catch (err) {
        reject(new Error("Failed to parse Excel: " + err.message));
      }
    };
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.readAsArrayBuffer(file);
  });
}

function mapRows(rows) {
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => String(h || "").trim().toLowerCase());
  const fieldIdx = {};
  headers.forEach((h, i) => {
    const f = HEADER_FIELD_MAP[h];
    if (f && !(f in fieldIdx)) fieldIdx[f] = i;
  });
  const get = (row, field) => String(row[fieldIdx[field]] ?? "").trim();
  return rows
    .slice(1)
    .map((row, i) => ({
      _rowNum: i + 2,
      title:          get(row, "title"),
      userDept:       get(row, "userDept"),
      entity:         get(row, "entity"),
      contractor:     get(row, "contractor"),
      category:       get(row, "category") || "General",
      priority:       get(row, "priority") || "Medium",
      status:         get(row, "status")   || "Not Started",
      dueDate:        get(row, "dueDate")  || null,
      lookAhead:      get(row, "lookAhead"),
      description:    get(row, "description"),
      assignedTo:     get(row, "assignedTo"),
      assignedToName: get(row, "assignedToName"),
      ownerEmail:     get(row, "ownerEmail"),
      ownerName:      get(row, "ownerName"),
    }))
    .filter((r) => r.title || r.userDept);
}

function validateRow(row, canAssign, currentUserEmail, currentUserName) {
  const errors = [];
  if (!row.title) errors.push("Description is required");
  if (!row.userDept) errors.push("User Department is required");
  if (canAssign && !row.assignedTo) errors.push("Assigned To Email is required");
  if (row.dueDate && isNaN(new Date(row.dueDate).getTime())) {
    errors.push(`Invalid due date "${row.dueDate}" — use YYYY-MM-DD`);
  }
  return {
    ...row,
    assignedTo:     canAssign ? row.assignedTo     : currentUserEmail,
    assignedToName: canAssign ? row.assignedToName : currentUserName,
    dueDate: row.dueDate && !isNaN(new Date(row.dueDate).getTime()) ? row.dueDate : null,
    _errors: errors,
  };
}

export default function ActivityBulkUpload() {
  const { isAdmin, isManager, isEditor, canEdit, currentUserEmail, currentUserName } = useUserAccess();
  const { addActivities, isDemoMode } = useActivityTracker();

  const canAssign = isAdmin || isManager || isEditor;

  const [file, setFile]             = useState(null);
  const [rows, setRows]             = useState([]);
  const [parsing, setParsing]       = useState(false);
  const [parseError, setParseError] = useState("");
  const [uploading, setUploading]   = useState(false);
  const [progress, setProgress]     = useState(0);
  const [total, setTotal]           = useState(0);
  const [result, setResult]         = useState(null);
  const fileRef = useRef(null);

  if (!isEditor) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Text size={500} weight="semibold" block style={{ marginBottom: 8 }}>Access Denied</Text>
        <Text style={{ color: "#888" }}>Editor or Admin access is required to bulk upload activities.</Text>
      </div>
    );
  }

  async function handleFileSelect(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setRows([]);
    setParsing(true);
    setParseError("");
    try {
      const rawRows = await parseExcelFile(f);
      const mapped = mapRows(rawRows);
      if (!mapped.length) {
        setParseError("No data rows found. Make sure the file matches the template format and has at least one data row below the header.");
        return;
      }
      setRows(mapped.map((r) => validateRow(r, canAssign, currentUserEmail, currentUserName)));
    } catch (err) {
      setParseError(err.message);
    } finally {
      setParsing(false);
    }
  }

  async function handleUpload() {
    const valid = rows.filter((r) => r._errors.length === 0);
    if (!valid.length) return;
    setUploading(true);
    setProgress(0);
    setTotal(valid.length);
    try {
      const payload = valid.map(({ _rowNum, _errors, ...rest }) => rest);
      const res = await addActivities(payload, {
        createdBy: currentUserEmail,
        createdByName: currentUserName,
        onProgress: (done, tot) => { setProgress(done); setTotal(tot); },
      });
      setResult(res);
    } catch (err) {
      setResult({ created: [], failed: [{ error: err.message }] });
    } finally {
      setUploading(false);
    }
  }

  function handleReset() {
    setFile(null);
    setRows([]);
    setResult(null);
    setParseError("");
    setProgress(0);
    setTotal(0);
    if (fileRef.current) fileRef.current.value = "";
  }

  const validCount   = rows.filter((r) => r._errors.length === 0).length;
  const invalidCount = rows.filter((r) => r._errors.length > 0).length;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: 20 }}>
        <Link to="/activity-tracker" style={{ textDecoration: "none", color: "#0066FF", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <ArrowLeft24Regular style={{ fontSize: 16 }} />Back to Activity Tracker
        </Link>
      </div>

      {/* Page title */}
      <div style={{ marginBottom: 28 }}>
        <Text block size={700} weight="bold" style={{ color: "#001A3D", marginBottom: 4 }}>Bulk Activity Upload</Text>
        <Text style={{ color: "#888", fontSize: 14 }}>
          Upload multiple activities at once using an Excel file.
          {isDemoMode && <Badge appearance="tint" color="warning" style={{ marginLeft: 10 }}>Demo Mode — data saved locally</Badge>}
        </Text>
      </div>

      {/* ── Step 1: Template ── */}
      <div style={cardStyle}>
        <StepLabel n={1} label="Download the Template" />
        <Text style={{ color: "#555", fontSize: 13, display: "block", marginBottom: 14 }}>
          Fill in the Excel template to prepare your data. Columns marked with <span style={{ color: "#D13438" }}>*</span> are required.
          {!canAssign && (
            <span style={{ color: "#005A9E" }}> The "Assigned To" columns will be auto-filled with your account since you are a Staff member.</span>
          )}
        </Text>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          {TEMPLATE_HEADERS.map((h) => (
            <span
              key={h}
              style={{
                fontSize: 11, padding: "3px 8px", borderRadius: 4,
                border: `1px solid ${h.endsWith("*") ? "#D13438" : "#EDEBE9"}`,
                color: h.endsWith("*") ? "#D13438" : "#555",
                background: h.endsWith("*") ? "#FFF4F4" : "#FAFAF9",
              }}
            >
              {h}
            </span>
          ))}
        </div>
        <Button
          icon={<ArrowDownload24Regular />}
          appearance="primary"
          onClick={downloadTemplate}
          style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}
        >
          Download Template
        </Button>
      </div>

      {/* ── Step 2: Upload ── */}
      <div style={cardStyle}>
        <StepLabel n={2} label="Upload Your File" />
        <Text style={{ color: "#555", fontSize: 13, display: "block", marginBottom: 16 }}>
          Fill in the template and upload it here. The system will validate each row before creating activities.
        </Text>

        {/* Drop zone (shown when no file selected) */}
        {!file && (
          <div
            style={{
              border: "2px dashed #C7C3C0", borderRadius: 10, padding: "44px 24px",
              textAlign: "center", cursor: "pointer", background: "#FAFAF9",
              transition: "border-color 0.15s",
            }}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFileSelect({ target: { files: e.dataTransfer.files } });
            }}
          >
            <ArrowUpload24Regular style={{ fontSize: 44, color: "#0066FF", marginBottom: 10 }} />
            <Text block weight="semibold" style={{ color: "#201F1E", marginBottom: 4 }}>Click to browse or drag & drop</Text>
            <Text style={{ color: "#888", fontSize: 12 }}>Accepts .xlsx and .xls files</Text>
          </div>
        )}

        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleFileSelect} />

        {/* Parsing spinner */}
        {parsing && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 16, color: "#555" }}>
            <Spinner size="small" /><Text>Parsing file…</Text>
          </div>
        )}

        {/* Error */}
        {parseError && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", background: "#FDE7E9", borderRadius: 8, color: "#D13438", fontSize: 13, marginTop: 12 }}>
            <Warning24Regular style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{parseError}</span>
          </div>
        )}

        {/* Selected file chip */}
        {file && !parsing && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#F3F2F1", borderRadius: 8 }}>
            <DocumentBulletList24Regular style={{ color: "#0066FF", flexShrink: 0 }} />
            <Text style={{ flex: 1, fontSize: 13 }}>{file.name}</Text>
            <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} onClick={handleReset} disabled={uploading} />
          </div>
        )}
      </div>

      {/* ── Step 3: Preview & Upload ── */}
      {rows.length > 0 && !result && (
        <div style={cardStyle}>
          <StepLabel n={3} label="Review & Upload" />

          {/* Summary badges */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <span style={{ padding: "6px 14px", borderRadius: 20, background: "#DFF6DD", color: "#107C10", fontWeight: 600, fontSize: 13 }}>
              {validCount} valid row{validCount !== 1 ? "s" : ""}
            </span>
            {invalidCount > 0 && (
              <span style={{ padding: "6px 14px", borderRadius: 20, background: "#FDE7E9", color: "#D13438", fontWeight: 600, fontSize: 13 }}>
                {invalidCount} invalid (will be skipped)
              </span>
            )}
          </div>

          {/* Upload progress bar */}
          {uploading && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <Text style={{ fontSize: 13, color: "#555" }}>Creating activities…</Text>
                <Text style={{ fontSize: 13, fontWeight: 600 }}>{progress} / {total}</Text>
              </div>
              <div style={{ height: 8, background: "#EDEBE9", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: total > 0 ? `${(progress / total) * 100}%` : "0%",
                  background: "linear-gradient(90deg,#0066FF,#00C4CC)",
                  borderRadius: 4,
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}

          {/* Preview table */}
          <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto", marginBottom: 18, border: "1px solid #EDEBE9", borderRadius: 8 }}>
            <Table size="small">
              <TableHeader>
                <TableRow>
                  {["#", "DESCRIPTION", "USER DEPT", "ENTITY", "CONTRACTOR", "ASSIGNED TO", "PRIORITY", "STATUS", "DUE DATE", ""].map((h) => (
                    <TableHeaderCell key={h} style={{ background: "#001A3D", color: "#fff", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", padding: "8px 10px" }}>
                      {h}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => {
                  const isInvalid = row._errors.length > 0;
                  return (
                    <TableRow key={row._rowNum} style={{ background: isInvalid ? "#FFF4F4" : i % 2 === 0 ? "#fff" : "#FAFAF9" }}>
                      <TableCell style={{ fontSize: 11, color: "#AAA", padding: "6px 10px" }}>{row._rowNum}</TableCell>
                      <TableCell style={{ padding: "6px 10px", minWidth: 200 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, color: row.title ? "#201F1E" : "#D13438" }}>
                          {row.title || "(empty)"}
                        </Text>
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: "6px 10px", minWidth: 120, color: row.userDept ? "#201F1E" : "#D13438" }}>
                        {row.userDept || "(empty)"}
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: "6px 10px", color: "#555", minWidth: 70 }}>{row.entity || "—"}</TableCell>
                      <TableCell style={{ fontSize: 12, padding: "6px 10px", color: "#555", minWidth: 120 }}>{row.contractor || "—"}</TableCell>
                      <TableCell style={{ fontSize: 12, padding: "6px 10px", minWidth: 130 }}>
                        {row.assignedToName || row.assignedTo
                          ? <span style={{ color: "#201F1E" }}>{row.assignedToName || row.assignedTo}</span>
                          : canAssign
                            ? <span style={{ color: "#D13438" }}>(none)</span>
                            : <span style={{ color: "#0066FF" }}>You</span>
                        }
                      </TableCell>
                      <TableCell style={{ padding: "6px 10px", minWidth: 70 }}>
                        <span style={{
                          fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                          background: row.priority === "High" ? "#FDE7E9" : row.priority === "Medium" ? "#FFF4CE" : "#EFF6FC",
                          color:      row.priority === "High" ? "#D13438" : row.priority === "Medium" ? "#8A5900" : "#005A9E",
                        }}>
                          {row.priority || "—"}
                        </span>
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: "6px 10px", color: "#555", minWidth: 90 }}>{row.status || "—"}</TableCell>
                      <TableCell style={{ fontSize: 12, padding: "6px 10px", color: "#555", minWidth: 80 }}>{row.dueDate || "—"}</TableCell>
                      <TableCell style={{ padding: "6px 10px" }}>
                        {isInvalid ? (
                          <Tooltip content={row._errors.join("; ")} relationship="label">
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer", color: "#D13438" }}>
                              <ErrorCircle20Regular />
                              <Text style={{ fontSize: 10, color: "#D13438" }}>{row._errors.length}</Text>
                            </span>
                          </Tooltip>
                        ) : (
                          <CheckmarkCircle20Regular style={{ color: "#107C10" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button appearance="secondary" onClick={handleReset} disabled={uploading}>
              Clear &amp; Re-upload
            </Button>
            <Button
              appearance="primary"
              icon={uploading ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
              onClick={handleUpload}
              disabled={uploading || validCount === 0}
              style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)", minWidth: 170 }}
            >
              {uploading
                ? `Uploading ${progress}/${total}…`
                : `Upload ${validCount} Activit${validCount !== 1 ? "ies" : "y"}`}
            </Button>
          </div>
        </div>
      )}

      {/* ── Result ── */}
      {result && (
        <div style={cardStyle}>
          <Text weight="semibold" size={400} block style={{ marginBottom: 16 }}>Upload Complete</Text>
          <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ padding: "12px 24px", borderRadius: 10, background: "#DFF6DD", color: "#107C10", fontWeight: 700, fontSize: 16 }}>
              {result.created.length} Created
            </div>
            {result.failed.length > 0 && (
              <div style={{ padding: "12px 24px", borderRadius: 10, background: "#FDE7E9", color: "#D13438", fontWeight: 700, fontSize: 16 }}>
                {result.failed.length} Failed
              </div>
            )}
          </div>

          {result.failed.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Text weight="semibold" style={{ color: "#D13438", fontSize: 13 }} block>Failed items:</Text>
              {result.failed.map((f, i) => (
                <div key={i} style={{ padding: "6px 12px", background: "#FFF4F4", borderRadius: 6, marginTop: 5, fontSize: 12, color: "#D13438" }}>
                  {f.item?.title || `Item ${i + 1}`}: {f.error}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 12 }}>
            <Button appearance="secondary" onClick={handleReset}>Upload More</Button>
            <Link to="/activity-tracker" style={{ textDecoration: "none" }}>
              <Button appearance="primary" style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}>
                View Activity Tracker
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #EDEBE9",
  padding: 24,
  marginBottom: 20,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

function StepLabel({ n, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{
        width: 28, height: 28, borderRadius: "50%", background: "#0066FF", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 700, fontSize: 13, flexShrink: 0,
      }}>
        {n}
      </div>
      {label && <Text weight="semibold" size={400}>{label}</Text>}
    </div>
  );
}
